'use client';

import { cn } from '@ngg/storefront-utils';
import { formatPrice } from '@utils/currency/formatPrice';
import { Dictionary } from '@ts/dictionary';
import Paragraph from '@ui/components/Paragraph/Paragraph';
import { XIcon } from '@ngg/icons';
import { useState, Fragment } from 'react';
import {
  ProductPageInfo,
  ProductPageInfoFragment,
  ProductPageInfoTextBlockFragment,
} from '@business/gql/graphql';
import { Dialog, Transition } from '@headlessui/react';
import { Heading } from '@ui/components/Heading/Heading';
import { RichText } from '@ui/components/RichText';

type PriceProps = {
  from?: string;
  price: number;
  dictionary: Dictionary;
  className?: string;
  priceType?: string;
  originalPrice?: number;
  recommendedPrice?: number;
  priceInfoBlock?: ProductPageInfoTextBlockFragment;
  cta: ProductPageInfoFragment['cta'];
};

export default function Price({
  from,
  price,
  dictionary,
  className,
  priceType,
  recommendedPrice,
  originalPrice = price,
  priceInfoBlock,
}: PriceProps) {
  const [tooltip, setTooltip] = useState(false);
  return (
    <>
      <div className={cn('relative font-sans', className)}>
        <div className="flex flex-col gap-1">
          {priceType === 'standard' && (
            <Paragraph className="text-lg font-medium xl:text-xl">
              {from}
              <span className="font-bold">{formatPrice(price)}</span>
            </Paragraph>
          )}
          {priceType === 'priceMatch' && (
            <>
              <Paragraph className="font-sans text-lg font-medium xl:text-xl">
                {from}
                <span className="font-bold text-red-600">
                  {formatPrice(price)}
                </span>
              </Paragraph>
              <Paragraph className="text-grey-300 inline-flex flex-wrap items-start gap-1 text-sm max-md:flex-col">
                <span>
                  {dictionary.recPrice}: {formatPrice(originalPrice)}
                </span>
                {priceInfoBlock && (
                  <button
                    onClick={() => setTooltip(true)}
                    className="text-grey-300 underline">
                    {dictionary.readMore}
                  </button>
                )}
              </Paragraph>
            </>
          )}
          {priceType === 'campaign' && (
            <>
              <Paragraph className="inline-flex items-center gap-1 font-sans">
                {from && <span className="text-lg lg:text-xl">{from}</span>}
                <strong className="text-lg font-bold text-red-600 lg:text-xl">
                  {formatPrice(price)}
                </strong>
                {recommendedPrice && (
                  <strong className="text-grey-300 text-lg font-bold line-through lg:text-xl">
                    {formatPrice(recommendedPrice)}
                  </strong>
                )}
              </Paragraph>
              {recommendedPrice &&
                originalPrice &&
                originalPrice < recommendedPrice && (
                  <Paragraph className="text-grey-300 inline-flex flex-wrap items-start gap-1 font-serif text-sm">
                    <span className="whitespace-nowrap">
                      {dictionary.previousPrice}: {formatPrice(originalPrice)}
                    </span>
                    {priceInfoBlock && (
                      <button
                        onClick={() => setTooltip(true)}
                        className="underline">
                        {dictionary.readMore}
                      </button>
                    )}
                  </Paragraph>
                )}
            </>
          )}
        </div>
      </div>
      <Transition appear show={tooltip} as={Fragment}>
        <Dialog
          open={tooltip}
          onClose={() => setTooltip(false)}
          className="relative z-40">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="relative mx-auto max-h-full max-w-xl items-start overflow-auto bg-white px-8 py-8 text-left prose-hr:my-6 md:py-12">
                <button
                  type="button"
                  onClick={() => setTooltip(false)}
                  className="absolute right-0 top-2 p-4">
                  <XIcon className="text-base" />
                </button>
                <Dialog.Title as={Heading} size={4} className="mb-4">
                  {priceInfoBlock?.title}
                </Dialog.Title>
                <RichText content={priceInfoBlock?.content} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
